/**
 * Kuchendiagramm (Donut) je Geometrie als Canvas — performant durch Cache
 * (§7.4 des Plans).
 *
 * Der Donut wird als HTMLCanvasElement gezeichnet und als `ol/style/Icon`
 * (`img: canvas`) platziert. Anteile werden auf 5 %-Schritte quantisiert und
 * das erzeugte Canvas unter einem quantisierten Schlüssel gecacht — so
 * entstehen nur wenige Dutzend distinct Canvases statt einer pro Feld, was
 * auf Tausende Felder skaliert (O(1) je Feature nach Cache-Warmlauf).
 */

import { KLASSEN, normiereVerteilung, Verteilung } from './klassen';

const cache = new Map<string, HTMLCanvasElement>();

/** Auf 5 %-Schritte quantisierte Anteile → stabiler, kompakter Cache-Schlüssel. */
function quantisiere(anteile: number[]): number[] {
  return anteile.map((a) => Math.round(a * 20)); // 0..20 (5 %-Raster)
}

export interface DonutOptions {
  /** Kantenlänge des Canvas in CSS-Pixeln (quadratisch). */
  size: number;
  /** Loch-Radius als Anteil des Außenradius (0..1). 0 = Vollkreis (Kuchen). Standard 0. */
  lochAnteil?: number;
  /** Prozent-Labels je Segment zeichnen (nur für große Diagramme sinnvoll). */
  zeigeLabels?: boolean;
  /** Geräte-Pixelverhältnis für scharfe Darstellung auf HiDPI. */
  dpr?: number;
}

/**
 * Liefert (gecacht) ein Donut-Canvas für die gegebene Verteilung.
 * Gibt null zurück, wenn keine verwertbare Verteilung vorliegt
 * (Donut wird dann ausgelassen — kein Fehler).
 */
export function makeDonutCanvas(
  verteilung: Verteilung | undefined,
  options: DonutOptions
): HTMLCanvasElement | null {
  const norm = normiereVerteilung(verteilung);
  if (!norm) return null;

  const size = options.size;
  const lochAnteil = options.lochAnteil ?? 0;
  const zeigeLabels = options.zeigeLabels ?? false;
  const dpr = options.dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

  const anteile = norm.map((n) => n.anteil);
  const q = quantisiere(anteile);
  const key = `${size}|${lochAnteil}|${zeigeLabels ? 1 : 0}|${dpr}|${q.join('-')}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(size * dpr);
  canvas.height = Math.round(size * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);

  const cx = size / 2;
  const cy = size / 2;
  const rAussen = size / 2 - 1; // 1 px Rand für Anti-Aliasing
  const rInnen = rAussen * lochAnteil;

  // Segmente in KLASSEN-Reihenfolge (Winkel = Anteil · 2π), Start bei -90°.
  let start = -Math.PI / 2;
  for (let i = 0; i < norm.length; i++) {
    const anteil = norm[i].anteil;
    if (anteil <= 0) continue;
    const end = start + anteil * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, rAussen, start, end);
    ctx.closePath();
    ctx.fillStyle = norm[i].farbe;
    ctx.fill();
    start = end;
  }

  // Innenloch nur bei Donut (lochAnteil > 0) — transparent ausstanzen,
  // damit die Basiskarte durchscheint (destination-out entfernt Pixel).
  if (rInnen > 0) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(cx, cy, rInnen, 0, Math.PI * 2);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();
  }

  // Feiner Außenring für Kontrast auf grauer Basiskarte.
  ctx.beginPath();
  ctx.arc(cx, cy, rAussen, 0, Math.PI * 2);
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(24,52,79,0.35)';
  ctx.stroke();

  // Feiner Innenring (Loch-Kante) im selben Stil — so ist der Donut auch am
  // inneren Rand sauber umrandet (nicht nur außen).
  if (rInnen > 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, rInnen, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(24,52,79,0.35)';
    ctx.stroke();
  }

  // Prozent-Labels je Segment (weiß mit dunkler Kontur), innen am Rand platziert.
  if (zeigeLabels) {
    const rLabel = rInnen > 0 ? (rAussen + rInnen) / 2 : rAussen * 0.62;
    const fontPx = Math.max(11, Math.round(size * 0.15));
    ctx.font = `700 ${fontPx}px 'Open Sans', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    let a = -Math.PI / 2;
    for (let i = 0; i < norm.length; i++) {
      const anteil = norm[i].anteil;
      if (anteil <= 0) {
        continue;
      }
      const mid = a + (anteil * Math.PI * 2) / 2;
      a += anteil * Math.PI * 2;
      if (anteil < 0.05) {
        continue; // zu schmal → Label würde überlappen
      }
      const lx = cx + rLabel * Math.cos(mid);
      const ly = cy + rLabel * Math.sin(mid);
      const text = `${Math.round(anteil * 100)}%`;
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(24,52,79,0.55)';
      ctx.strokeText(text, lx, ly);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(text, lx, ly);
    }
  }

  cache.set(key, canvas);
  return canvas;
}

/** Für Tests/Debug: aktuelle Cache-Größe. */
export function donutCacheGroesse(): number {
  return cache.size;
}

export { KLASSEN };
